import { FocusEvent, KeyboardEvent, ReactElement, useEffect, useRef, useState } from 'react'
import { Box, Input } from '../Checkbox/styles'
import { salesReportIcons } from '../icons'
import { SelectOption } from '../SelectField'
import * as S from './styles'

interface MultiSelectFieldProps {
  id: string
  label: string
  options: SelectOption[]
  value: string[]
  onChange: (value: string[]) => void
  allLabel?: string
  maxSelected?: number
  dense?: boolean
  align?: 'start' | 'end'
  autoFocusSearch?: boolean
  inline?: boolean
}

const SEARCH_THRESHOLD = 6

const normalizeText = (text: string): string =>
  text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()

const selectedCount = (count: number): string => `${count} ${count === 1 ? 'selecionado' : 'selecionados'}`

export const multiSelectSummary = (value: string[], options: SelectOption[], allLabel: string): string => {
  const selected = options.filter((option) => value.includes(option.value))
  if (selected.length === 0) return allLabel
  if (selected.length === 1) return selected[0].label
  return selectedCount(selected.length)
}

export const normalizeSelection = (draft: string[], options: SelectOption[]): string[] => {
  const chosen = new Set(draft)
  const ordered = options.filter((option) => chosen.has(option.value)).map((option) => option.value)
  return ordered.length === options.length ? [] : ordered
}

const sameSelection = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((item, index) => item === b[index])

function OptionCheckbox({
  label,
  checked,
  disabled = false,
  strong = false,
  onToggle,
}: {
  label: string
  checked: boolean
  disabled?: boolean
  strong?: boolean
  onToggle: () => void
}): ReactElement {
  return (
    <S.OptionLabel $disabled={disabled}>
      <Input type="checkbox" checked={checked} disabled={disabled} onChange={onToggle} />
      <Box $checked={checked} aria-hidden="true">
        {checked && <img src={salesReportIcons.check} alt="" width={11} height={11} />}
      </Box>
      <S.OptionText $strong={strong}>{label}</S.OptionText>
    </S.OptionLabel>
  )
}

export function MultiSelectField({
  id,
  label,
  options,
  value,
  onChange,
  allLabel = 'Todos',
  maxSelected,
  dense = false,
  align = 'start',
  autoFocusSearch = true,
  inline = false,
}: MultiSelectFieldProps): ReactElement {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<string[]>([])
  const [query, setQuery] = useState('')
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const firstFocusRef = useRef<HTMLInputElement>(null)
  const popoverId = `${id}-options`
  const current = normalizeSelection(value, options)
  const searchable = options.length >= SEARCH_THRESHOLD

  const openPopover = (): void => {
    setDraft(current)
    setQuery('')
    setOpen(true)
  }

  const close = (): void => {
    setOpen(false)
    const next = normalizeSelection(draft, options)
    if (!sameSelection(next, current)) onChange(next)
  }

  const closeRef = useRef(close)
  closeRef.current = close

  useEffect(() => {
    if (!open) return undefined
    if (autoFocusSearch) firstFocusRef.current?.focus()
    const onPointerDown = (event: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) closeRef.current()
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [open, autoFocusSearch])

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== 'Escape' || !open) return
    event.preventDefault()
    close()
    triggerRef.current?.focus()
  }

  const onBlur = (event: FocusEvent<HTMLDivElement>): void => {
    const next = event.relatedTarget as Node | null
    if (open && next != null && !event.currentTarget.contains(next)) close()
  }

  const chosen = new Set(draft)
  const limitReached = maxSelected != null && chosen.size >= maxSelected
  const term = normalizeText(query.trim())
  const visible = term === '' ? options : options.filter((option) => normalizeText(option.label).includes(term))

  const toggle = (optionValue: string): void => {
    setDraft((previous) =>
      previous.includes(optionValue) ? previous.filter((item) => item !== optionValue) : [...previous, optionValue]
    )
  }

  return (
    <S.Field ref={rootRef} onKeyDown={onKeyDown} onBlur={onBlur}>
      <S.Label htmlFor={id} $dense={dense}>
        {label}
      </S.Label>
      <S.Trigger
        ref={triggerRef}
        id={id}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
        $placeholder={current.length === 0}
        onClick={() => (open ? close() : openPopover())}
      >
        <S.Summary>{multiSelectSummary(current, options, allLabel)}</S.Summary>
        <S.Chevron src={salesReportIcons.chevronDown} alt="" width={16} height={16} aria-hidden="true" $open={open} />
      </S.Trigger>
      {open && (
        <S.Popover id={popoverId} role="dialog" aria-label={`Opções de ${label}`} tabIndex={-1} $align={align} $inline={inline}>
          {searchable && (
            <S.Search
              ref={firstFocusRef}
              type="search"
              placeholder="Buscar"
              aria-label={`Buscar ${label.toLowerCase()}`}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          )}
          <S.List>
            <S.AllItem>
              <OptionCheckbox label={allLabel} checked={draft.length === 0} strong onToggle={() => setDraft([])} />
            </S.AllItem>
            {visible.map((option) => {
              const checked = chosen.has(option.value)
              return (
                <li key={option.value}>
                  <OptionCheckbox
                    label={option.label}
                    checked={checked}
                    disabled={!checked && limitReached}
                    onToggle={() => toggle(option.value)}
                  />
                </li>
              )
            })}
          </S.List>
          {visible.length === 0 && <S.Note>Nenhum resultado para a busca.</S.Note>}
          {limitReached && (
            <S.Note role="status">
              Até {maxSelected} por vez. Para ver tudo, use “{allLabel}”.
            </S.Note>
          )}
          <S.Footer>
            <S.Count>{draft.length === 0 ? allLabel : selectedCount(draft.length)}</S.Count>
            <S.ApplyButton
              type="button"
              onClick={() => {
                close()
                triggerRef.current?.focus()
              }}
            >
              Aplicar
            </S.ApplyButton>
          </S.Footer>
        </S.Popover>
      )}
    </S.Field>
  )
}
